const MODULE_LOAD_RECOVERY_KEY = 'x3algo-dashboard-module-load-recovery'
const MODULE_LOAD_RETRY_WINDOW_MS = 30_000

const MODULE_LOAD_ERROR_PATTERNS = [
  /failed to fetch dynamically imported module/i,
  /error loading dynamically imported module/i,
  /importing a module script failed/i,
  /loading (?:css )?chunk [\w-]+ failed/i,
  /chunkloaderror/i,
  /unable to preload css/i
]

let inMemoryRecoveryAttempt = null

const errorText = (error) => {
  if (typeof error === 'string') return error

  return [error?.name, error?.message, error?.stack].filter(Boolean).join(' ')
}

export const isModuleLoadError = (error) => {
  const text = errorText(error)
  return MODULE_LOAD_ERROR_PATTERNS.some((pattern) => pattern.test(text))
}

const recoveryFingerprint = (error) => {
  const text = errorText(error)
  const assetUrl = text.match(/https?:\/\/[^\s)]+\/assets\/[^\s)]+/i)?.[0]
  return (assetUrl || text).slice(0, 1000)
}

export const recoverFromModuleLoadError = (
  error,
  {
    storage,
    location = globalThis.location,
    now = Date.now,
    onBeforeReload
  } = {}
) => {
  if (!isModuleLoadError(error) || !location?.reload) return false

  const fingerprint = recoveryFingerprint(error)
  const attemptedAt = now()
  let recoveryStorage = storage
  let previousAttempt = inMemoryRecoveryAttempt

  try {
    if (recoveryStorage === undefined) {
      recoveryStorage = globalThis.sessionStorage
    }
    const storedAttempt = JSON.parse(
      recoveryStorage?.getItem(MODULE_LOAD_RECOVERY_KEY) || 'null'
    )
    if (storedAttempt) previousAttempt = storedAttempt
  } catch {
    recoveryStorage = null
  }

  const duplicateAttempt =
    previousAttempt?.fingerprint === fingerprint &&
    attemptedAt - previousAttempt.at < MODULE_LOAD_RETRY_WINDOW_MS
  if (duplicateAttempt) return false

  const nextAttempt = { fingerprint, at: attemptedAt }
  inMemoryRecoveryAttempt = nextAttempt
  try {
    recoveryStorage?.setItem(
      MODULE_LOAD_RECOVERY_KEY,
      JSON.stringify(nextAttempt)
    )
  } catch {
    // The in-memory guard still prevents duplicate events in this document.
  }

  try {
    onBeforeReload?.(error)
  } catch {
    // Error reporting must not prevent recovery from a broken deployment.
  }

  location.reload()
  return true
}

export const installModuleLoadRecovery = ({
  eventTarget = globalThis.window,
  storage,
  location = globalThis.location,
  now = Date.now,
  onBeforeReload
} = {}) => {
  if (!eventTarget?.addEventListener) return () => {}

  const handlePreloadError = (event) => {
    const recoveryRequested = recoverFromModuleLoadError(event.payload, {
      storage,
      location,
      now,
      onBeforeReload
    })

    if (recoveryRequested) event.preventDefault()
  }

  eventTarget.addEventListener('vite:preloadError', handlePreloadError)
  return () =>
    eventTarget.removeEventListener('vite:preloadError', handlePreloadError)
}
